import * as Yup from 'yup';
import AllForm from '../../stateless/Form';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  UseCreateChoiceGroup,
  useEditChoiceGroup,
} from '../../Query/ChoiceGroupQuery';

import { useAtom } from 'jotai';
import { choiceGroupAtom } from './ManageChoiceGroupTable';
import { useEffect, useMemo, useState } from 'react';

import React from 'react';
import { useAllStores } from '../../Query/ManageStoreQuery';

import { FieldProps, FormikProps } from 'formik';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useManageCmsParams } from './useManageCmsParams';
import { useChoiceList } from '../../Query/ChoiceListQuery';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Validation schema depends on add/edit mode


const validationSchema = (t: TFunction) => Yup.object().shape({
  choiceGroupNameEnglish: Yup.string()
    .required(t('choiceGroup.formValidation.requiredChoiceGroupName'))
    .test(
      'contains-letter-or-number',
      t('choiceGroup.formValidation.choiceGroupNameLetterOrNumber'),
      (value) => typeof value === 'string' && /[A-Za-z0-9]/.test(value)
    )
    .max(100, t('choiceGroup.formValidation.choiceGroupNameMax'))
    .test(
      'not-only-spaces',
      t('choiceGroup.formValidation.choiceGroupNameNotOnlySpaces'),
      (value) => typeof value === 'string' && value.trim().length > 0
    ),

  choiceGroupNameArabic: Yup.string()
    .required(t('choiceGroup.formValidation.requiredChoiceGroupNameArabic'))
    .matches(
      /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
      t('choiceGroup.formValidation.choiceGroupNameArabicLetters')
    )
    .max(50, t('choiceGroup.formValidation.choiceGroupNameArabicMax'))
    .test(
      'not-only-spaces',
      t('choiceGroup.formValidation.choiceGroupNameArabicNotOnlySpaces'),
      (value) => typeof value === 'string' && value.trim().length > 0
    ),

  minimumLimit: Yup.number()
    .typeError(t('choiceGroup.formValidation.minimumLimitNumber'))
    .required(t('choiceGroup.formValidation.minimumLimitRequired'))
    .max(99, t('choiceGroup.formValidation.maximumLimitMax'))
    .test(
      'based-on-choice-group-option',
      function (value) {
        const { choiceGroupOption } = this.parent;
        const requiredOption = t('choiceGroup.form.required');
        const optionalOption = t('choiceGroup.form.optional');

        if (choiceGroupOption === requiredOption && (value === undefined || value < 1)) {
          return this.createError({
            message: t('choiceGroup.formValidation.minimumLimitRequiredForRequiredOption')
          });
        }
        if (choiceGroupOption === optionalOption && value !== 0) {
          return this.createError({
            message: t('choiceGroup.formValidation.minimumLimitRequiredForOptionalOption')
          });
        }
        return true;
      }
    )
    .test(
      'is-less-than-or-equal-to-max',
      t('choiceGroup.formValidation.minimumLimitLTE'),
      function (value) {
        const { maximumLimit } = this.parent;
        return value !== undefined && maximumLimit !== undefined ? value <= maximumLimit : true;
      }
    )
    .test(
      'not-exceed-choices-length',
      t('choiceGroup.formValidation.minimumLimitNotExceedChoices'),
      function (value) {
        const { choiceDetails } = this.parent;
        return value !== undefined && choiceDetails && Array.isArray(choiceDetails)
          ? value <= choiceDetails.length
          : true;
      }
    ),

  maximumLimit: Yup.number()
    .typeError(t('choiceGroup.formValidation.maximumLimitNumber'))
    .required(t('choiceGroup.formValidation.maximumLimitRequired'))
    .positive(t('choiceGroup.formValidation.maximumLimitPositive'))
    .max(99, t('choiceGroup.formValidation.maximumLimitMax'))
    .test(
      'is-greater-than-or-equal-to-min',
      function (value) {
        const { minimumLimit } = this.parent;
        return value !== undefined && minimumLimit !== undefined && value < minimumLimit
          ? this.createError({ message: t('choiceGroup.formValidation.maximumLimitGTE') })
          : true;
      }
    )
    .test(
      'not-exceed-choices-length',
      t('choiceGroup.formValidation.maximumLimitNotExceedChoices'),
      function (value) {
        const { choiceDetails } = this.parent;
        return value !== undefined && choiceDetails && Array.isArray(choiceDetails)
          ? value <= choiceDetails.length
          : true;
      }
    ),

  storeId: Yup.string()
    .required(t('choiceGroup.formValidation.requiredStore')),

  choiceDetails: Yup.array()
    .of(Yup.string())
    .required(t('choiceGroup.formValidation.requiredChoice'))
    .test(
      'no-choices-available',
      t('choiceGroup.formValidation.noChoicesAvailable'),
      function (value) {
        const { noChoicesAvailable } = this.options.context || {};
        return noChoicesAvailable ? false : value && value.length > 0;
      }
    ),

  choiceGroupOption: Yup.string()
    .required(t('choiceGroup.formValidation.requiredChoiceGroupOption')),
});

const AddChoiceGroup = ({ onCancel }: { onCancel: () => void }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const [open, setOpen] = React.useState(false);
  const [choiceGroupDataId] = useAtom(choiceGroupAtom);
  const { warning } = useThemeColors();

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  const { mutate: CreateChoiceGroup, isPending: CreateChoiceGrouLoading } =
    UseCreateChoiceGroup();
  const { mutate: EditChoiceGroup, isPending: EditChoiceGroupLoading } =
    useEditChoiceGroup();

  const { params } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const [selectedStoreId, setSelectedStoreId] = useState(choiceGroupDataId?.storeId ?? '');
  const { data: choiceListData } = useChoiceList(selectedStoreId ? { storeId: selectedStoreId } : {});

  const [storeType, setStoreType] = useState('');

  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string; storeType: string }[]>([]);

  const {
    data: storesData
  } = useAllStores(memoizedParams);

  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.nameEnglish,
        storeType: store.restaurantDetails.storeType,
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  const choiceDataOption = choiceListData?.map((item: any) => ({
    value: item.choiceId,
    label: item.choiceName,
  }));

  // Determine if there are no choices available for the selected store
  const noChoicesAvailable = !choiceDataOption || choiceDataOption.length === 0;


  return (
    <>
      {AllForm({
        initialValues: {
          choiceGroupNameEnglish:
            choiceGroupDataId?.choiceGroupNameEnglish ?? '',
          choiceGroupNameArabic: choiceGroupDataId?.choiceGroupNameArabic ?? '',
          minimumLimit: choiceGroupDataId?.minimumLimit ?? 
            (choiceGroupDataId?.choiceGroupOption === false ? 0 : 1), // Default to 1 since choiceGroupOption defaults to "required"
          maximumLimit: choiceGroupDataId?.maximumLimit ?? null,
          // merchantId: choiceGroupDataId?.merchantDetails?.merchantId ?? '',
          storeId: choiceGroupDataId?.storeId ?? '',
          choiceDetails: choiceGroupDataId?.choiceDetails ?? [],
          choiceGroupOption:
            choiceGroupDataId?.choiceGroupOption === true
              ? t('choiceGroup.form.required')
              : choiceGroupDataId?.choiceGroupOption === false
                ? t('choiceGroup.form.optional')
                : t('choiceGroup.form.required'),
          storeType: choiceGroupDataId?.storeType ?? ''
        },
        validationSchema: validationSchema(t),
        validationContext: { noChoicesAvailable },
        onSubmit: async (values: any) => {
          // For edit, use values from choiceGroupDataId; for add, use form values
          const modifiedValues = choiceGroupDataId
            ? {
              choiceGroupNameEnglish: values.choiceGroupNameEnglish,
              choiceGroupNameArabic: values.choiceGroupNameArabic,
              minimumLimit: +values.minimumLimit,
              maximumLimit: +values.maximumLimit,
              choiceGroupOption: values.choiceGroupOption === t('choiceGroup.form.required'),
              choiceDetails: values.choiceDetails,
              merchantList: [choiceGroupDataId.merchantDetails?.merchantId],
              storeId: choiceGroupDataId.storeId,
              storeType: choiceGroupDataId.storeType,
              addedBy: "Merchant"
            }
            : {
              choiceGroupNameEnglish: values.choiceGroupNameEnglish,
              choiceGroupNameArabic: values.choiceGroupNameArabic,
              minimumLimit: +values.minimumLimit,
              maximumLimit: +values.maximumLimit,
              choiceGroupOption: values.choiceGroupOption === t('choiceGroup.form.required'),
              choiceDetails: values.choiceDetails,
              merchantList: [values.merchantId],
              storeId: values.storeId,
              storeType: storeType,
              addedBy: "Merchant"
            };
          try {
            if (choiceGroupDataId) {
              EditChoiceGroup(
                {
                  id: choiceGroupDataId.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                    // closeForm();
                    // resetForm();
                  },
                  onError: (error: any) => {
                    console.error('Error updating choice group:', error);
                    // Handle error appropriately, e.g., show a message to the user
                  },
                }
              );
            } else {
              CreateChoiceGroup(
                { data: modifiedValues, storeId: values.storeId },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.error('Error creating choice group:', error);
                    // Handle error appropriately, e.g., show a message to the user
                  },
                }
              );
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            // Handle error appropriately, e.g., show a message to the user
          }
        },

        fields: [
          {
            name: 'choiceGroupNameEnglish',
            label: t('choiceGroup.form.choiceGroupName'),
            type: 'text',
            isRequired: true,
          },
          {
            name: 'choiceGroupNameArabic',
            label: t('choiceGroup.form.choiceGroupNameArabic'),
            type: 'text',
            isRequired: true,
          },
          {
            name: 'choiceGroupOption',
            label: t('choiceGroup.form.choiceGroupOption'),
            type: 'custom',
            isRequired: true,
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
              const requiredOption = t('choiceGroup.form.required');
              const optionalOption = t('choiceGroup.form.optional');
              
              const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value;
                form.setFieldValue(field.name, newValue);
                
                // Update minimumLimit based on choiceGroupOption
                const currentMin = form.values.minimumLimit;
                if (newValue === requiredOption) {
                  // If required, set minimumLimit to 1 if it's currently 0, null, or empty
                  if (currentMin === null || currentMin === 0 || currentMin === '' || currentMin === undefined) {
                    form.setFieldValue('minimumLimit', 1);
                  } else if (currentMin < 1) {
                    // If value is less than 1 (invalid for required), set to 1
                    form.setFieldValue('minimumLimit', 1);
                  }
                } else if (newValue === optionalOption) {
                  // If optional, minimum limit must always be 0
                  form.setFieldValue('minimumLimit', 0);
                }
              };

              return (
                <FormControl>
                  <RadioGroup
                    row
                    name={field.name}
                    value={field.value || requiredOption}
                    onChange={handleChange}
                    onBlur={field.onBlur}
                    sx={{ gap: 2.5, mt: 0.5 }}
                  >
                    <FormControlLabel
                      value={requiredOption}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: COLORS.text.secondary,
                          }}
                        >
                          {requiredOption}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value={optionalOption}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: COLORS.text.secondary,
                          }}
                        >
                          {optionalOption}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              );
            },
          },
          ...(!choiceGroupDataId ? [
            {
              name: 'storeId',
              label: t('choiceGroup.form.selectStore'),
              type: 'select',
              isRequired: true,
              component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                return (
                  <Box sx={{ width: 400 }}>
                    <Autocomplete
                      options={storeOptions}
                      getOptionLabel={(option) => option.label || ''}
                      value={storeOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(_, newValue) => {
                        const value = newValue ? newValue.value : '';
                        form.setFieldValue('storeId', value);
                        setStoreType(newValue ? newValue?.storeType : '')
                        setSelectedStoreId(value as string);
                      }}
                      fullWidth
                      sx={{
                        width: '100%', // Ensure Autocomplete itself expands
                        '& .MuiInputBase-root': {
                          width: '100%', // Force input root width
                        },
                      }}

                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('choiceGroup.form.placeholders.selectStore')}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                );
              },
            },

          ] : []),
          {
            name: 'choiceDetails',
            label: t('choiceGroup.form.addChoice'),
            type: 'hello',
            isRequired: true,
            component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
              const selectedCategoryIds: string[] = Array.isArray(field.value)
                ? field.value
                : [];
              const hasError =
                form.touched[field.name] && Boolean(form.errors[field.name]);

              const handleSelectChange = (
                event: SelectChangeEvent<string[]>
              ) => {
                const { value } = event.target;
                const newValue =
                  typeof value === 'string' ? value.split(',') : value;

                // ✅ Set value and trigger validation
                form.setFieldTouched(field.name, true, true);
                form.setFieldValue(field.name, newValue, true);

                // ✅ Close dropdown after selection
                setOpen(false);
              };

              const handleChipDelete = (categoryIdToDelete: string) => () => {
                const newSelectedCategoryIds = selectedCategoryIds.filter(
                  (categoryId) => categoryId !== categoryIdToDelete
                );
                form.setFieldValue(field.name, newSelectedCategoryIds, true);
                form.setFieldTouched(field.name, true, true);
              };

              const selectedCategories = (choiceDataOption || []).filter(
                (option: any) => selectedCategoryIds.includes(option.value)
              );

              const isChoiceSelectDisabled = !selectedStoreId || noChoicesAvailable;
              const shouldBlur = isChoiceSelectDisabled;
              return (
                <FormControl error={hasError} fullWidth sx={{ width: '401px' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Select Dropdown */}
                    <Select
                      multiple
                      open={open}
                      onOpen={() => setOpen(true)}
                      onClose={() => setOpen(false)}
                      value={selectedCategoryIds}
                      onChange={handleSelectChange}
                      input={<OutlinedInput />}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return (
                            <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>
                              {!selectedStoreId
  ? t("choiceGroup.form.selectStoreFirst")
  : noChoicesAvailable
    ? t("choiceGroup.form.noChoiceAvailable")
    : t("choiceGroup.form.addChoice")}
                            </span>
                          );
                        }
                        return '';
                      }}
                      sx={{
                        width: '401px',
                        filter: shouldBlur ? 'blur(1px)' : 'none',
                        backgroundColor: shouldBlur ? '#f5f5f5' : 'inherit',
                        minWidth: 'unset',
                      }}
                      disabled={isChoiceSelectDisabled}
                    >
                      <MenuItem value="" disabled>
                        <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                          {!selectedStoreId
                            ? `-- ${t('choiceGroup.form.placeholders.selectStoreFirst')} --`
                            : noChoicesAvailable
                              ? 'No choice available'
                              : '-- Select Choice --'}
                        </span>
                      </MenuItem>
                      {!noChoicesAvailable && selectedStoreId && (choiceDataOption || []).map((option: any) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>

                    {/* Chips shown in a new row below the select */}
                    {selectedCategories.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {selectedCategories.map((category: any) => (
                          <Chip
                            key={category.value}
                            label={category.label}
                            onDelete={handleChipDelete(category.value)}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </FormControl>
              );
            },
          },
          {
            name: 'minimumLimit',
            label: t('choiceGroup.form.minimumLimit'),
            type: 'number',
            isRequired: true,
          },
          {
            name: 'maximumLimit',
            label: t('choiceGroup.form.maximumLimit'),
            type: 'number',
            isRequired: true,
          },
       
        

        ],

        // customButton: (
        //   <Button
        //     type="submit"
        //     variant="contained"
        //     sx={{
        //       textTransform: 'none',
        //       fontSize: 14,
        //       px: 3,
        //       py: 1,
        //       backgroundColor: 'rgb(76, 10, 39)',
        //       '&:hover': {
        //         backgroundColor: 'rgb(76, 10, 39, 0.8)',
        //       },
        //       '&:disabled': {
        //         backgroundColor: 'rgba(0, 0, 0, 0.12)',
        //         color: 'rgba(0, 0, 0, 0.26)',
        //       },
        //     }}
        //   >
        //     Create
        //   </Button>
        // ),
        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': {
                  backgroundColor: COLORS.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={() => formikProps.handleSubmit()}
              disabled={CreateChoiceGrouLoading || EditChoiceGroupLoading}
            >
              {CreateChoiceGrouLoading || EditChoiceGroupLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {choiceGroupDataId ? t('choiceGroup.form.updating') : t('choiceGroup.form.creating')}
                </>
              ) : choiceGroupDataId ? (
                t('choiceGroup.form.update')
              ) : (
                t('choiceGroup.form.create')
              )}
            </Button>

            {!choiceGroupDataId && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  formikProps.resetForm();
                  formikProps.setFieldValue('storeId', '');
                  setStoreType(''); 
                  setSelectedStoreId(''); 
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: COLORS.secondary.main,
                  color: COLORS.secondary.main,
                  backgroundColor:"transparent",
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {t('choiceGroup.form.reset')}
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              {t('choiceGroup.form.cancel')}
            </Button>
          </Box>
        ),
      })()}
      ;
    </>
  );
};

export default AddChoiceGroup;  
